import { useParams, Link } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import Navbar from '@/components/feature/Navbar';
import MangaCard from '@/components/feature/MangaCard';
import Footer from '@/components/feature/Footer';
import { mockMangaList } from '@/mocks/manga';

export default function MangaDetail() {
  const { id } = useParams<{ id: string }>();
  const manga = mockMangaList.find((m) => m.id === id);

  if (!manga) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="min-h-screen pt-16 flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-file-unknow-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-heading text-foreground-900 mb-2">漫画未找到</h1>
            <p className="text-foreground-500 mb-6">该漫画不存在或已被移除</p>
            <Link
              to="/"
              className="inline-block px-6 py-2 bg-primary-500 text-background-50 rounded-full text-sm hover:bg-primary-600 transition-colors"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const formatViews = (v: number): string => {
    if (v >= 10000) return `${(v / 10000).toFixed(1)}万`;
    return v.toString();
  };

  const relatedManga = mockMangaList
    .filter((m) => m.id !== manga.id && (m.categorySlug === manga.categorySlug || m.tags.some((t) => manga.tags.includes(t))))
    .slice(0, 6);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${manga.title} - 香蕉漫画网免费在线漫画`,
    description: manga.description,
    author: {
      '@type': 'Person',
      name: manga.author,
    },
    datePublished: manga.chapters[0]?.updateTime || manga.updateTime,
    dateModified: manga.updateTime,
    image: manga.coverUrl,
    publisher: {
      '@type': 'Organization',
      name: '香蕉漫画网',
    },
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>

      <Navbar />
      <main className="min-h-screen pt-16">
        <div className="bg-background-100/70 border-b border-background-200/70">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: manga.category, href: `/category/${manga.categorySlug}` },
                { label: manga.title },
              ]}
            />
          </div>
        </div>

        <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-8">
          <article className="flex flex-col lg:flex-row gap-8">
            <div className="lg:w-[300px] shrink-0">
              <div className="sticky top-24">
                <div className="rounded-lg overflow-hidden border border-background-200/70">
                  <img
                    src={manga.coverUrl}
                    alt={`${manga.title} 漫画封面`}
                    title={`${manga.title} - 免费漫画在线观看`}
                    className="w-full aspect-[3/4] object-cover object-top"
                  />
                </div>
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-3">
                {manga.title}
              </h1>

              <div className="flex flex-wrap items-center gap-3 mb-4">
                <span className="text-sm text-foreground-600">
                  <strong className="text-foreground-900">作者：</strong>{manga.author}
                </span>
                <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  manga.status === '连载中'
                    ? 'bg-primary-100 text-primary-700'
                    : 'bg-secondary-100 text-secondary-700'
                }`}>
                  {manga.status}
                </span>
                <span className="flex items-center gap-1 text-sm text-accent-600">
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-star-fill text-accent-500"></i>
                  </span>
                  <strong className="text-foreground-900">{manga.rating}</strong>
                </span>
                <span className="text-xs text-foreground-500 flex items-center gap-1">
                  <span className="w-3 h-3 flex items-center justify-center">
                    <i className="ri-eye-line"></i>
                  </span>
                  {formatViews(manga.views)} 次阅读
                </span>
              </div>

              <div className="flex flex-wrap gap-1.5 mb-6">
                {manga.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 rounded-full text-xs bg-accent-100 text-accent-700 font-medium"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <div className="bg-background-100/70 rounded-lg p-4 md:p-5 mb-8">
                <h2 className="font-heading text-foreground-900 font-semibold mb-2 text-sm">
                  <a href="#chapters">作品简介</a>
                </h2>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  {manga.description}
                </p>
                <p className="text-xs text-foreground-400 mt-3">
                  最后更新：{manga.updateTime}
                </p>
              </div>

              <div id="chapters" className="mb-10">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-heading text-foreground-900 font-bold text-lg">
                    章节列表
                  </h2>
                  <span className="text-xs text-foreground-500">
                    共 {manga.chapters.length} 话
                  </span>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                  {manga.chapters.map((ch) => (
                    <a
                      key={ch.id}
                      href={`/manga/${manga.id}#chapter-${ch.number}`}
                      className="flex items-center justify-between p-3 rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-300 hover:bg-primary-50 transition-all duration-200 cursor-pointer"
                    >
                      <span className="text-sm text-foreground-800 font-medium truncate">
                        {ch.title}
                      </span>
                      <span className="text-[10px] text-foreground-400 ml-2 shrink-0">
                        {ch.updateTime}
                      </span>
                    </a>
                  ))}
                </div>
              </div>

              {relatedManga.length > 0 && (
                <div className="border-t border-background-200/70 pt-8">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="font-heading text-foreground-900 font-bold text-lg">
                      相关推荐
                    </h2>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                    {relatedManga.map((m) => (
                      <MangaCard key={m.id} {...m} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </article>
        </div>
      </main>
      <Footer />
    </>
  );
}